import React, { useState, useEffect } from 'react';
import { Header } from '../components/Header';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { FiUsers, FiUserPlus, FiDollarSign, FiUserMinus, FiTrendingUp, FiTrendingDown, FiSave, FiPercent, FiArrowLeft, FiStar, FiRefreshCw } from 'react-icons/fi';
import { toast } from 'sonner';
import { formatBRL } from '../utils/format';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PIE_COLORS = ['#1E3A8A', '#16A34A', '#F59E0B', '#7C3AED', '#DC2626'];

export const Gerenciar = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Prices
  const [basicPrice, setBasicPrice] = useState('10.00');
  const [essentialPrice, setEssentialPrice] = useState('37.90');
  const [savingPrice, setSavingPrice] = useState(false);
  
  // Full page view for subscribers
  const [showAllSubsView, setShowAllSubsView] = useState(false);
  
  const [subFilter, setSubFilter] = useState('active');
  const [adjustingEmail, setAdjustingEmail] = useState('');
  const [adjustDays, setAdjustDays] = useState('');
  const [filterStartDate, setFilterStartDate] = useState(`${new Date().getFullYear()}-01-01`);
  const [filterEndDate, setFilterEndDate] = useState(`${new Date().getFullYear()}-12-31`);

  // Discount adjustment modal state
  const [discountModalOpen, setDiscountModalOpen] = useState(false);
  const [selectedSubForDiscount, setSelectedSubForDiscount] = useState(null);
  const [discountType, setDiscountType] = useState('percentage'); // 'percentage' | 'fixed'
  const [discountValue, setDiscountValue] = useState('');
  const [savingDiscount, setSavingDiscount] = useState(false);

  // Search filter for subscribers
  const [searchName, setSearchName] = useState('');

  // Updating user plan state
  const [updatingUserPlanEmail, setUpdatingUserPlanEmail] = useState('');

  useEffect(() => { loadStats(); }, []);

  const loadStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`, { withCredentials: true });
      setStats(response.data);
      const bPrice = response.data.subscription_price_basic ?? response.data.subscription_price ?? 10.0;
      const ePrice = response.data.subscription_price_essential ?? 37.90;
      setBasicPrice(Number(bPrice).toFixed(2));
      setEssentialPrice(Number(ePrice).toFixed(2));
    } catch (error) {
      console.error('Error loading admin stats:', error);
      if (error?.response?.status === 403) {
        toast.error('Acesso restrito a administradores');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSavePrice = async () => {
    setSavingPrice(true);
    try {
      const numBasic = parseFloat(String(basicPrice).replace(',', '.'));
      const numEssential = parseFloat(String(essentialPrice).replace(',', '.'));
      
      if (isNaN(numBasic) || numBasic < 0) {
        toast.error('Valor da Assinatura Básica inválido');
        return;
      }
      if (isNaN(numEssential) || numEssential < 0) {
        toast.error('Valor da Assinatura Essencial inválido');
        return;
      }

      await axios.put(
        `${API}/admin/subscription-price`,
        { basic_price: numBasic, essential_price: numEssential },
        { withCredentials: true }
      );
      setBasicPrice(numBasic.toFixed(2));
      setEssentialPrice(numEssential.toFixed(2));
      toast.success('Preços atualizados com sucesso!');
      loadStats();
    } catch (error) {
      console.error('Error saving subscription prices:', error);
      toast.error('Erro ao salvar preços');
    } finally {
      setSavingPrice(false);
    }
  };

  const handleAdjustDays = async (email) => {
    const days = parseInt(adjustDays);
    if (!days || isNaN(days)) {
      toast.error('Informe a quantidade de dias');
      return;
    }
    try {
      const response = await axios.post(`${API}/admin/adjust-days`, { email, days }, { withCredentials: true });
      toast.success(response.data.message);
      setAdjustingEmail('');
      setAdjustDays('');
      loadStats();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erro ao ajustar dias');
    }
  };

  const handleUpdateUserPlan = async (email, newPlan) => {
    setUpdatingUserPlanEmail(email);
    try {
      const response = await axios.post(
        `${API}/admin/user-subscription-plan`,
        { email, plan_type: newPlan },
        { withCredentials: true }
      );
      toast.success(response.data.message || 'Plano do usuário atualizado!');
      loadStats();
    } catch (error) {
      console.error('Error updating user plan:', error);
      toast.error(error?.response?.data?.detail || 'Erro ao alterar plano do usuário');
    } finally {
      setUpdatingUserPlanEmail('');
    }
  };

  const handleOpenDiscountModal = (sub) => {
    setSelectedSubForDiscount(sub);
    setDiscountType(sub.discount_type || 'percentage');
    setDiscountValue(sub.discount_value ? String(sub.discount_value) : '');
    setDiscountModalOpen(true);
  };

  const handleSaveDiscount = async () => {
    if (!selectedSubForDiscount) return;
    const numVal = parseFloat(String(discountValue).replace(',', '.'));
    if (isNaN(numVal) || numVal <= 0) {
      handleRemoveDiscount();
      return;
    }
    if (discountType === 'percentage' && (numVal <= 0 || numVal > 100)) {
      toast.error('Informe uma porcentagem entre 0% e 100%');
      return;
    }
    setSavingDiscount(true);
    try {
      await axios.post(
        `${API}/admin/user-discount`,
        {
          email: selectedSubForDiscount.email,
          user_id: selectedSubForDiscount.user_id,
          discount_type: discountType,
          discount_value: numVal
        },
        { withCredentials: true }
      );
      toast.success('Desconto atualizado com sucesso!');
      setDiscountModalOpen(false);
      loadStats();
    } catch (error) {
      console.error('Error saving discount:', error);
      toast.error(error?.response?.data?.detail || 'Erro ao salvar desconto');
    } finally {
      setSavingDiscount(false);
    }
  };

  const handleRemoveDiscount = async () => {
    if (!selectedSubForDiscount) return;
    setSavingDiscount(true);
    try {
      await axios.post(
        `${API}/admin/user-discount`,
        {
          email: selectedSubForDiscount.email,
          user_id: selectedSubForDiscount.user_id,
          discount_type: null,
          discount_value: 0
        },
        { withCredentials: true }
      );
      toast.success('Desconto removido!');
      setDiscountModalOpen(false);
      loadStats();
    } catch (error) {
      console.error('Error removing discount:', error);
      toast.error(error?.response?.data?.detail || 'Erro ao remover desconto');
    } finally {
      setSavingDiscount(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const filteredSubs = (stats?.all_subscribers || []).filter(s => {
    if (subFilter === 'active') {
      if (s.status !== 'active') return false;
    } else if (subFilter === 'inactive') {
      if (s.status === 'active') return false;
    }
    // Search by Name or Email
    if (searchName.trim()) {
      const term = searchName.trim().toLowerCase();
      const matchName = (s.name || '').toLowerCase().includes(term);
      const matchEmail = (s.email || '').toLowerCase().includes(term);
      if (!matchName && !matchEmail) return false;
    }
    // Date filter
    if (s.paid_at && filterStartDate && filterEndDate) {
      const paidDate = s.paid_at.slice(0, 10);
      if (paidDate < filterStartDate || paidDate > filterEndDate) return false;
    }
    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F0F5FA] dark:bg-[#070D1A] transition-colors">
        <Header />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900 mx-auto"></div>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="min-h-screen bg-[#F0F5FA] dark:bg-[#070D1A] transition-colors">
        <Header />
        <div className="max-w-7xl mx-auto px-4 py-12 text-center">
          <p className="text-slate-600">Acesso restrito.</p>
        </div>
      </div>
    );
  }

  // ================= VIEW: TODOS OS ASSINANTES (TELA CHEIA) =================
  if (showAllSubsView) {
    return (
      <div className="min-h-screen bg-[#F0F5FA] dark:bg-[#070D1A] transition-colors">
        <Header />
        <main className="transition-all duration-300">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
          {/* Top Bar with Back Button */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <button
                onClick={() => setShowAllSubsView(false)}
                className="inline-flex items-center gap-2 text-sm font-bold text-blue-900 dark:text-cyan-400 hover:opacity-80 transition-opacity mb-2"
              >
                <FiArrowLeft size={18} /> Voltar para Gerenciar
              </button>
              <h1 className="text-3xl sm:text-4xl font-black text-blue-900 dark:text-white">Assinantes</h1>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Gerencie todos os clientes, altere planos, adicione dias e configure descontos personalizados.
              </p>
            </div>
            
            <div className="bg-white dark:bg-slate-900 px-4 py-2.5 rounded-xl border border-blue-900/10 dark:border-white/10 shadow-sm flex items-center gap-3">
              <span className="text-xs text-slate-500 font-semibold uppercase">Total exibido:</span>
              <span className="text-lg font-black text-blue-900 dark:text-cyan-400">{filteredSubs.length}</span>
            </div>
          </div>

          {/* Filters Card */}
          <Card className="bg-white dark:bg-slate-900 border border-blue-900/10 dark:border-white/10 shadow-sm">
            <CardContent className="p-4 sm:p-5">
              <div className="flex flex-wrap items-center gap-3 sm:gap-4">
                {/* Search */}
                <div className="flex-1 min-w-[240px]">
                  <Label htmlFor="search-subs-full" className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-1">
                    Buscar Assinante
                  </Label>
                  <div className="relative">
                    <Input
                      id="search-subs-full"
                      type="text"
                      placeholder="Buscar por nome ou email..."
                      value={searchName}
                      onChange={(e) => setSearchName(e.target.value)}
                      className="bg-slate-50 dark:bg-slate-800 text-sm h-10 pr-8"
                    />
                    {searchName && (
                      <button
                        type="button"
                        onClick={() => setSearchName('')}
                        className="absolute right-3 top-2.5 text-slate-400 hover:text-slate-600 font-bold"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                </div>

                {/* Status Pills */}
                <div>
                  <Label className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-1">
                    Status
                  </Label>
                  <div className="flex items-center gap-1.5 bg-slate-100 dark:bg-slate-800 p-1 rounded-lg">
                    {['active', 'inactive', 'all'].map(f => (
                      <button
                        key={f}
                        onClick={() => setSubFilter(f)}
                        className={`text-xs px-3 py-1.5 rounded-md font-bold transition-all ${
                          subFilter === f
                            ? 'bg-blue-900 text-white shadow-sm'
                            : 'text-slate-600 dark:text-slate-300 hover:text-blue-900'
                        }`}
                      >
                        {f === 'active' ? 'Ativos' : f === 'inactive' ? 'Inativos' : 'Todos'}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Date Filter */}
                <div className="w-full sm:w-auto">
                  <Label className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-1">
                    Período de Pagamento
                  </Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="date"
                      value={filterStartDate}
                      onChange={e => setFilterStartDate(e.target.value)}
                      className="text-xs bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-white/10 rounded-lg px-2.5 py-2"
                    />
                    <span className="text-xs text-slate-400 font-bold">até</span>
                    <input
                      type="date"
                      value={filterEndDate}
                      onChange={e => setFilterEndDate(e.target.value)}
                      className="text-xs bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-white/10 rounded-lg px-2.5 py-2"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Subscribers Table Card */}
          <Card className="bg-white dark:bg-slate-900 border border-blue-900/10 dark:border-white/10 shadow-sm overflow-hidden">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50/80 dark:bg-slate-800/60 border-b border-slate-200/80 dark:border-white/10">
                      <TableHead className="font-bold text-blue-900 dark:text-white py-3.5">Nome</TableHead>
                      <TableHead className="font-bold text-blue-900 dark:text-white">Email</TableHead>
                      <TableHead className="font-bold text-blue-900 dark:text-white">Pagamento</TableHead>
                      <TableHead className="font-bold text-blue-900 dark:text-white">Data</TableHead>
                      <TableHead className="font-bold text-blue-900 dark:text-white text-center">Dias</TableHead>
                      <TableHead className="font-bold text-blue-900 dark:text-white text-center">Assinatura</TableHead>
                      <TableHead className="font-bold text-blue-900 dark:text-white text-center">Desconto</TableHead>
                      <TableHead className="font-bold text-blue-900 dark:text-white text-center">Adicionar Dias</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredSubs.map((sub, i) => (
                      <TableRow key={i} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/40 border-b border-slate-100 dark:border-white/5">
                        <TableCell className="font-bold text-blue-900 dark:text-white">{sub.name}</TableCell>
                        <TableCell className="text-xs text-slate-600 dark:text-slate-300 font-mono">{sub.email}</TableCell>
                        <TableCell>
                          <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${
                            sub.payment_method === 'PIX' ? 'bg-green-100 text-green-700 dark:bg-green-950/60 dark:text-green-300' :
                            sub.payment_method === 'Crédito' ? 'bg-blue-100 text-blue-700 dark:bg-blue-950/60 dark:text-blue-300' :
                            sub.payment_method === 'Débito' ? 'bg-purple-100 text-purple-700 dark:bg-purple-950/60 dark:text-purple-300' :
                            'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300'
                          }`}>
                            {sub.payment_method}
                          </span>
                        </TableCell>
                        <TableCell className="text-xs text-slate-600 dark:text-slate-400">{formatDate(sub.paid_at)}</TableCell>
                        
                        {/* Coluna Dias */}
                        <TableCell className="text-center">
                          {sub.status === 'active' ? (
                            <span className={`text-sm font-bold ${sub.days_remaining <= 7 ? 'text-red-600' : sub.days_remaining <= 15 ? 'text-yellow-600' : 'text-green-600 dark:text-green-400'}`}>
                              {sub.days_remaining} dias
                            </span>
                          ) : (
                            <span className="text-xs text-red-600 font-bold bg-red-50 dark:bg-red-950/50 px-2 py-0.5 rounded">Inativo</span>
                          )}
                        </TableCell>

                        {/* Nova Coluna Assinatura (Plano: Básico / Essencial com Select) */}
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center">
                            <Select
                              value={sub.plan_type || 'essential'}
                              onValueChange={(val) => handleUpdateUserPlan(sub.email, val)}
                              disabled={updatingUserPlanEmail === sub.email}
                            >
                              <SelectTrigger className="h-8 w-32 text-xs font-bold bg-white dark:bg-slate-800 border-slate-200 dark:border-white/10">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {sub.plan_type === 'trial' && (
                                  <SelectItem value="trial" className="text-xs font-semibold text-emerald-600 dark:text-emerald-400">
                                    Período de Teste
                                  </SelectItem>
                                )}
                                <SelectItem value="basic" className="text-xs font-semibold">
                                  Básico
                                </SelectItem>
                                <SelectItem value="essential" className="text-xs font-bold text-cyan-600 dark:text-cyan-400">
                                  Essencial
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </TableCell>

                        {/* Coluna Desconto */}
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-1.5">
                            {sub.discount_value > 0 ? (
                              <span className="inline-flex items-center text-xs font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300">
                                {sub.discount_type === 'percentage' ? `${sub.discount_value}% OFF` : `-${formatBRL(sub.discount_value)}`}
                              </span>
                            ) : (
                              <span className="text-xs text-slate-400">Sem desconto</span>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              data-testid={`adjust-discount-btn-${sub.email}`}
                              onClick={() => handleOpenDiscountModal(sub)}
                              className="h-7 px-2 text-xs text-blue-900 dark:text-cyan-400 hover:bg-blue-50 dark:hover:bg-blue-950/40 font-semibold"
                            >
                              Ajustar
                            </Button>
                          </div>
                        </TableCell>

                        {/* Coluna Adicionar Dias */}
                        <TableCell className="text-center">
                          {adjustingEmail === sub.email ? (
                            <div className="flex items-center justify-center gap-1">
                              <Input
                                type="number"
                                value={adjustDays}
                                onChange={(e) => setAdjustDays(e.target.value)}
                                placeholder="+30"
                                className="w-20 h-7 text-xs"
                              />
                              <Button size="sm" className="h-7 px-2 bg-blue-900 text-white text-xs font-bold" onClick={() => handleAdjustDays(sub.email)}>OK</Button>
                              <Button size="sm" variant="ghost" className="h-7 px-1 text-xs" onClick={() => { setAdjustingEmail(''); setAdjustDays(''); }}>X</Button>
                            </div>
                          ) : (
                            <button
                              onClick={() => { setAdjustingEmail(sub.email); setAdjustDays(''); }}
                              className="text-xs text-blue-900 dark:text-cyan-400 font-semibold hover:underline"
                            >
                              Ajustar
                            </button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {filteredSubs.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center text-slate-500 py-12">
                          Nenhum assinante encontrado com os filtros selecionados.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {/* Modal de Ajuste de Desconto */}
          <Dialog open={discountModalOpen} onOpenChange={setDiscountModalOpen}>
            <DialogContent className="sm:max-w-md bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10">
              <DialogHeader>
                <DialogTitle className="text-xl font-bold text-blue-900 dark:text-white">
                  Ajustar Desconto
                </DialogTitle>
                <DialogDescription className="text-xs text-slate-500">
                  Defina um desconto especial para {selectedSubForDiscount?.name || 'este assinante'} ({selectedSubForDiscount?.email}).
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-2">
                <div>
                  <Label className="text-sm font-semibold text-blue-900 dark:text-white mb-2 block">
                    Tipo de Desconto
                  </Label>
                  <div className="grid grid-cols-2 gap-2 bg-slate-100 dark:bg-slate-800 p-1 rounded-lg">
                    <button
                      type="button"
                      data-testid="discount-type-percent"
                      onClick={() => setDiscountType('percentage')}
                      className={`py-2 px-3 rounded-md text-xs font-bold transition-all flex items-center justify-center gap-1.5 ${
                        discountType === 'percentage'
                          ? 'bg-blue-900 text-white shadow-sm'
                          : 'text-slate-600 dark:text-slate-300 hover:text-blue-900'
                      }`}
                    >
                      <FiPercent size={14} />
                      Desconto por %
                    </button>
                    <button
                      type="button"
                      data-testid="discount-type-fixed"
                      onClick={() => setDiscountType('fixed')}
                      className={`py-2 px-3 rounded-md text-xs font-bold transition-all flex items-center justify-center gap-1.5 ${
                        discountType === 'fixed'
                          ? 'bg-blue-900 text-white shadow-sm'
                          : 'text-slate-600 dark:text-slate-300 hover:text-blue-900'
                      }`}
                    >
                      <FiDollarSign size={14} />
                      Desconto por R$
                    </button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="discount_value_input" className="text-sm font-semibold text-blue-900 dark:text-white mb-1 block">
                    {discountType === 'percentage' ? 'Porcentagem de Desconto (%)' : 'Valor do Desconto (R$)'}
                  </Label>
                  <div className="relative">
                    <Input
                      id="discount_value_input"
                      data-testid="discount-value-input"
                      type="number"
                      min="0"
                      max={discountType === 'percentage' ? "100" : undefined}
                      step={discountType === 'percentage' ? "1" : "0.50"}
                      placeholder={discountType === 'percentage' ? "Ex: 10 (para 10%)" : "Ex: 2.00 (para R$ 2,00)"}
                      value={discountValue}
                      onChange={(e) => setDiscountValue(e.target.value)}
                      className="text-base font-semibold pr-10 bg-slate-50 dark:bg-slate-800"
                    />
                    <div className="absolute right-3 top-2.5 text-xs font-bold text-slate-400">
                      {discountType === 'percentage' ? '%' : 'R$'}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between gap-2 pt-2">
                {selectedSubForDiscount?.discount_value > 0 ? (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleRemoveDiscount}
                    disabled={savingDiscount}
                    className="text-red-600 hover:bg-red-50 hover:text-red-700 text-xs border-red-200"
                  >
                    Remover Desconto
                  </Button>
                ) : (
                  <div />
                )}
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setDiscountModalOpen(false)}
                    disabled={savingDiscount}
                  >
                    Cancelar
                  </Button>
                  <Button
                    data-testid="save-discount-button"
                    type="button"
                    onClick={handleSaveDiscount}
                    disabled={savingDiscount}
                    className="bg-blue-900 hover:bg-blue-800 text-white font-bold"
                  >
                    {savingDiscount ? 'Salvando...' : 'Salvar Desconto'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
        </main>
      </div>
    );
  }

  // ================= VIEW: PAINEL GERENCIAR PRINCIPAL =================
  const statCards = [
    {
      title: 'Assinantes Ativos',
      value: stats.active_subscribers,
      diff: stats.active_diff,
      diffLabel: 'Este Mês',
      icon: FiUsers,
      positive: stats.active_diff >= 0
    },
    {
      title: 'Novos Assinantes',
      value: stats.new_subscribers ?? stats.new_subscriptions ?? 0,
      diff: stats.new_diff,
      diffLabel: 'Este Mês',
      icon: FiUserPlus,
      positive: stats.new_diff >= 0
    },
    {
      title: 'Receita Mensal (MRR)',
      value: formatBRL(stats.mrr ?? stats.revenue_this_month ?? 0),
      diff: stats.mrr_diff != null ? (stats.mrr_diff >= 0 ? `+${formatBRL(stats.mrr_diff)}` : formatBRL(stats.mrr_diff)) : `+${stats.revenue_change ?? 0}%`,
      diffLabel: 'Este Mês',
      icon: FiTrendingUp,
      positive: (stats.mrr_diff ?? stats.revenue_change ?? 0) >= 0,
      isMoney: true
    },
    {
      title: 'Cancelamentos',
      value: stats.cancellations,
      diff: stats.churn_rate,
      diffLabel: `Churn ${stats.churn_rate}%`,
      icon: FiUserMinus,
      positive: stats.cancellations <= (stats.cancellations - stats.cancellations_diff),
      isChurn: true
    }
  ];

  return (
    <div className="min-h-screen bg-[#F0F5FA] dark:bg-[#070D1A] transition-colors">
      <Header />
      <main className="transition-all duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-4xl font-black text-blue-900 dark:text-white mb-8">Gerenciar</h1>

        {/* Stat Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {statCards.map((card, i) => (
            <Card key={i} data-testid={`admin-card-${i}`} className="bg-white dark:bg-slate-900 border border-blue-900/10 dark:border-white/10 shadow-sm">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-sm font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider">{card.title}</p>
                  <card.icon className="text-blue-900 dark:text-cyan-400" size={20} />
                </div>
                <p className="text-3xl font-black text-blue-900 dark:text-white mb-2">{card.value}</p>
                <div className="flex items-center gap-1">
                  {card.positive ? (
                    <FiTrendingUp className="text-green-600 dark:text-green-400" size={14} />
                  ) : (
                    <FiTrendingDown className="text-red-600" size={14} />
                  )}
                  <span className={`text-sm font-semibold ${card.positive ? 'text-green-600 dark:text-green-400' : 'text-red-600'}`}>
                    {card.isChurn ? card.diffLabel : card.isPercent ? `${card.diff >= 0 ? '+' : ''}${card.diff}${card.diffLabel}` : `${card.diff >= 0 ? '+' : ''}${card.diff} ${card.diffLabel}`}
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Revenue Evolution */}
          <Card className="bg-white dark:bg-slate-900 border border-blue-900/10 dark:border-white/10 shadow-sm">
            <CardHeader>
              <CardTitle className="text-xl font-semibold text-blue-900 dark:text-white">Evolução da Receita</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={stats.revenue_evolution}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="month" stroke="#475569" fontSize={12} />
                  <YAxis stroke="#475569" fontSize={12} />
                  <Tooltip
                    formatter={(value) => [formatBRL(value), 'Receita']}
                    contentStyle={{ backgroundColor: '#FFF', border: '1px solid #E2E8F0', borderRadius: '8px' }}
                  />
                  <defs>
                    <linearGradient id="revenueGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#1E3A8A" stopOpacity={0.2} />
                      <stop offset="100%" stopColor="#1E3A8A" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <Line type="monotone" dataKey="value" stroke="#1E3A8A" strokeWidth={3} dot={{ r: 5, fill: '#1E3A8A', stroke: '#FFF', strokeWidth: 2 }} activeDot={{ r: 7 }} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Payment Methods Pie */}
          <Card className="bg-white dark:bg-slate-900 border border-blue-900/10 dark:border-white/10 shadow-sm">
            <CardHeader>
              <CardTitle className="text-xl font-semibold text-blue-900 dark:text-white">Formas de Pagamento</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-6">
                <div className="space-y-3 min-w-[120px]">
                  {stats.payment_methods.map((pm, i) => (
                    <div key={pm.name} className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }}></div>
                      <span className="text-sm text-slate-700 dark:text-slate-300 font-medium">{pm.name}</span>
                      <span className="text-sm text-slate-500">{pm.percentage}%</span>
                    </div>
                  ))}
                </div>
                <div className="flex-1">
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie
                        data={stats.payment_methods}
                        cx="50%"
                        cy="50%"
                        outerRadius={90}
                        dataKey="value"
                        label={({ percentage }) => `${percentage}%`}
                        labelLine={false}
                      >
                        {stats.payment_methods.map((_, i) => (
                          <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value, name, props) => [props.payload.percentage + '%', props.payload.name]} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Bottom Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Latest Subscribers (Column 1-2) */}
          <Card className="lg:col-span-2 bg-white dark:bg-slate-900 border border-blue-900/10 dark:border-white/10 shadow-sm">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-xl font-semibold text-blue-900 dark:text-white">Últimos Assinantes</CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAllSubsView(true)}
                  className="border-blue-900 text-blue-900 dark:text-cyan-400 dark:border-cyan-500/30 hover:bg-blue-50 dark:hover:bg-cyan-950/30 font-bold"
                >
                  Ver todos os assinantes
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="border-b border-slate-200/80 dark:border-white/10">
                    <TableHead className="font-bold text-blue-900 dark:text-white">Nome</TableHead>
                    <TableHead className="font-bold text-blue-900 dark:text-white text-right">Valor</TableHead>
                    <TableHead className="font-bold text-blue-900 dark:text-white">Pagamento</TableHead>
                    <TableHead className="font-bold text-blue-900 dark:text-white">Data</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stats.latest_subscribers.map((sub, i) => (
                    <TableRow key={i} data-testid={`latest-sub-${i}`} className="border-b border-slate-100 dark:border-white/5">
                      <TableCell className="font-medium text-blue-900 dark:text-white">{sub.name}</TableCell>
                      <TableCell className="text-right font-semibold text-blue-900 dark:text-cyan-300">{formatBRL(sub.value)}</TableCell>
                      <TableCell>
                        <span className={`text-xs font-bold px-2 py-1 rounded ${
                          sub.payment_method === 'PIX' ? 'bg-green-100 text-green-700 dark:bg-green-950/60 dark:text-green-300' :
                          sub.payment_method === 'Crédito' ? 'bg-blue-100 text-blue-700 dark:bg-blue-950/60 dark:text-blue-300' :
                          sub.payment_method === 'Débito' ? 'bg-purple-100 text-purple-700 dark:bg-purple-950/60 dark:text-purple-300' :
                          'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300'
                        }`}>
                          {sub.payment_method}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm text-slate-600 dark:text-slate-400">{formatDate(sub.paid_at)}</TableCell>
                    </TableRow>
                  ))}
                  {stats.latest_subscribers.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-slate-500 py-8">Nenhum assinante ainda</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Subscription Price Management Card (Column 3) */}
          <Card className="bg-white dark:bg-slate-900 border border-blue-900/10 dark:border-white/10 shadow-sm">
            <CardHeader>
              <CardTitle className="text-xl font-semibold text-blue-900 dark:text-white">Gerenciar Preço da Assinatura</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Valor da Assinatura Básica */}
              <div>
                <Label className="text-blue-900 dark:text-white font-semibold text-xs uppercase tracking-wider">
                  Valor da Assinatura Básica:
                </Label>
                <div className="flex items-center gap-2 mt-1.5">
                  <span className="text-base font-bold text-blue-900 dark:text-cyan-400">R$</span>
                  <Input
                    data-testid="admin-basic-price-input"
                    value={basicPrice}
                    onChange={(e) => setBasicPrice(e.target.value)}
                    className="text-xl font-black text-blue-900 dark:text-white h-12 bg-slate-50 dark:bg-slate-800"
                    placeholder="10.00"
                  />
                </div>
              </div>

              {/* Valor da Assinatura Essencial */}
              <div>
                <Label className="text-blue-900 dark:text-white font-semibold text-xs uppercase tracking-wider flex items-center gap-1.5">
                  <FiStar className="text-amber-500" size={13} />
                  Valor da Assinatura Essencial:
                </Label>
                <div className="flex items-center gap-2 mt-1.5">
                  <span className="text-base font-bold text-blue-900 dark:text-cyan-400">R$</span>
                  <Input
                    data-testid="admin-essential-price-input"
                    value={essentialPrice}
                    onChange={(e) => setEssentialPrice(e.target.value)}
                    className="text-xl font-black text-blue-900 dark:text-white h-12 bg-slate-50 dark:bg-slate-800"
                    placeholder="37.90"
                  />
                </div>
              </div>

              <Button
                data-testid="admin-save-price"
                onClick={handleSavePrice}
                disabled={savingPrice}
                className="w-full bg-blue-900 hover:bg-blue-800 text-white py-3 flex items-center justify-center gap-2 font-bold mt-2"
              >
                <FiSave size={18} />
                {savingPrice ? 'Salvando...' : 'Salvar Preços'}
              </Button>
              <p className="text-xs text-slate-500 text-center">Os novos preços serão aplicados para novas contratações e renovações.</p>
            </CardContent>
          </Card>
        </div>
      </div>
      </main>
    </div>
  );
};
